import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Badge,
  Button,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider
} from '@mui/joy';
import {
  Notifications as NotificationsIcon,
  FilterList,
  MarkEmailRead,
  MoreVert,
  Schedule,
  Edit,
  SwapHoriz,
  Info,
  Alarm,
  Circle,
  CheckCircle,
  Delete
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { NotificationService } from '../services/notificationService';
import { Notification, NotificationQuery, NotificationFilter, CATEGORY_DISPLAY_NAMES, PRIORITY_COLORS, CATEGORY_ICONS } from '../types/notification';

interface NotificationWidgetProps {
  className?: string;
  maxItems?: number;
  showFilter?: boolean;
}

interface NotificationWidgetState {
  notifications: Notification[];
  filter: NotificationFilter;
  loading: boolean;
  unreadCount: number;
  selectedNotification: Notification | null;
  modalOpen: boolean;
  anchorEl: HTMLElement | null;
  selectedNotificationId: number | null;
}

const NotificationWidget: React.FC<NotificationWidgetProps> = ({
  className,
  maxItems = 5,
  showFilter = true
}) => {
  const [state, setState] = useState<NotificationWidgetState>({
    notifications: [],
    filter: 'all',
    loading: true,
    unreadCount: 0,
    selectedNotification: null,
    modalOpen: false,
    anchorEl: null,
    selectedNotificationId: null
  });

  const fetchNotifications = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      
      const query: NotificationQuery = {
        limit: maxItems,
        filter: state.filter
      };
      
      const response = await NotificationService.getNotifications(query);
      
      setState(prev => ({
        ...prev,
        notifications: response.notifications,
        unreadCount: response.unreadCount,
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [maxItems, state.filter]);

  const markAsRead = useCallback(async (id: number, isRead: boolean = true) => {
    try {
      await NotificationService.markAsRead(id, isRead);
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [fetchNotifications]);

  const markAllAsRead = useCallback(async () => {
    try {
      await NotificationService.markAllAsRead();
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }, [fetchNotifications]);

  const deleteNotification = useCallback(async (id: number) => {
    try {
      await NotificationService.deleteNotification(id);
      await fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [fetchNotifications]);

  const handleFilterChange = useCallback((filter: NotificationFilter) => {
    setState(prev => ({ ...prev, filter }));
  }, []);

  const handleNotificationClick = useCallback((notification: Notification) => {
    setState(prev => ({
      ...prev,
      selectedNotification: notification,
      modalOpen: true
    }));
    
    // Mark as read when opened
    if (!notification.is_read) {
      markAsRead(notification.id, true);
    }
  }, [markAsRead]);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, notificationId: number) => {
    setState(prev => ({
      ...prev,
      anchorEl: event.currentTarget,
      selectedNotificationId: notificationId
    }));
  }, []);

  const handleMenuClose = useCallback(() => {
    setState(prev => ({
      ...prev,
      anchorEl: null,
      selectedNotificationId: null
    }));
  }, []);

  const handleMenuAction = useCallback(async (action: 'mark_read' | 'mark_unread' | 'delete') => {
    if (!state.selectedNotificationId) return;

    try {
      switch (action) {
        case 'mark_read':
          await markAsRead(state.selectedNotificationId, true);
          break;
        case 'mark_unread':
          await markAsRead(state.selectedNotificationId, false);
          break;
        case 'delete':
          await deleteNotification(state.selectedNotificationId);
          break;
      }
      handleMenuClose();
    } catch (error) {
      console.error('Error performing menu action:', error);
    }
  }, [state.selectedNotificationId, markAsRead, deleteNotification, handleMenuClose]);

  const getCategoryIcon = useCallback((category: string) => {
    const iconName = CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS];
    switch (iconName) {
      case 'Schedule':
        return <Schedule />;
      case 'Edit':
        return <Edit />;
      case 'SwapHoriz':
        return <SwapHoriz />;
      case 'Info':
        return <Info />;
      case 'Alarm':
        return <Alarm />;
      default:
        return <NotificationsIcon />;
    }
  }, []);

  const formatTimeAgo = useCallback((dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Handle clicking outside the menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (state.anchorEl && !state.anchorEl.contains(event.target as Node)) {
        handleMenuClose();
      }
    };

    if (state.anchorEl) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state.anchorEl, handleMenuClose]);

  return (
    <Card className={className} variant="outlined">
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            <Typography level="h4" component="h2">
              Notifications
            </Typography>
            {state.unreadCount > 0 && (
              <Badge badgeContent={state.unreadCount} color="danger">
                <Circle sx={{ fontSize: 16 }} />
              </Badge>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {showFilter && (
              <Menu
                anchorEl={state.anchorEl}
                open={Boolean(state.anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem onClick={() => handleFilterChange('all')}>
                  All
                </MenuItem>
                <MenuItem onClick={() => handleFilterChange('unread')}>
                  Unread
                </MenuItem>
                <MenuItem onClick={() => handleFilterChange('read')}>
                  Read
                </MenuItem>
              </Menu>
            )}
            
            <Button
              size="sm"
              variant="outlined"
              startDecorator={<MarkEmailRead />}
              onClick={markAllAsRead}
              disabled={state.unreadCount === 0}
            >
              Mark All Read
            </Button>
          </Box>
        </Box>

        {/* Notifications List */}
        {state.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size="sm" />
          </Box>
        ) : state.notifications.length === 0 ? (
          <Alert variant="soft" color="neutral">
            {state.filter === 'unread' 
              ? 'No unread notifications'
              : state.filter === 'read'
              ? 'No read notifications'
              : 'No notifications yet'
            }
          </Alert>
        ) : (
          <List size="sm">
            {state.notifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'background.level1'
                  }
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <ListItemDecorator>
                  <Box sx={{ position: 'relative' }}>
                    {getCategoryIcon(notification.category)}
                    {!notification.is_read && (
                      <Circle
                        sx={{
                          position: 'absolute',
                          top: -4,
                          right: -4,
                          fontSize: 12,
                          color: 'primary.500'
                        }}
                      />
                    )}
                  </Box>
                </ListItemDecorator>
                
                <ListItemContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography level="body-sm" fontWeight="md" noWrap>
                      {notification.title}
                    </Typography>
                    <Chip
                      size="sm"
                      variant="soft"
                      sx={{
                        backgroundColor: PRIORITY_COLORS[notification.priority],
                        color: 'white',
                        fontSize: '0.75rem'
                      }}
                    >
                      {notification.priority}
                    </Chip>
                  </Box>
                  
                  <Typography level="body-xs" color="neutral" noWrap>
                    {notification.message}
                  </Typography>
                  
                  <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
                    {formatTimeAgo(notification.created_at)}
                  </Typography>
                </ListItemContent>
                
                <IconButton
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuOpen(e, notification.id);
                  }}
                >
                  <MoreVert />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}

        {/* View All Link */}
        {state.notifications.length > 0 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              size="sm"
              variant="plain"
              onClick={() => window.location.href = '/notifications'}
            >
              View All Notifications
            </Button>
          </Box>
        )}
      </CardContent>

      {/* Notification Modal */}
      <Modal open={state.modalOpen} onClose={() => setState(prev => ({ ...prev, modalOpen: false }))}>
        <ModalDialog size="md">
          <ModalClose />
          <DialogTitle>
            {state.selectedNotification?.title}
          </DialogTitle>
          <DialogContent>
            {state.selectedNotification && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip
                    size="sm"
                    variant="soft"
                    sx={{
                      backgroundColor: PRIORITY_COLORS[state.selectedNotification.priority],
                      color: 'white'
                    }}
                  >
                    {state.selectedNotification.priority}
                  </Chip>
                  <Typography level="body-sm" color="neutral">
                    {CATEGORY_DISPLAY_NAMES[state.selectedNotification.category as keyof typeof CATEGORY_DISPLAY_NAMES]}
                  </Typography>
                  <Typography level="body-sm" color="neutral">
                    {formatTimeAgo(state.selectedNotification.created_at)}
                  </Typography>
                </Box>
                
                <Typography level="body-md" sx={{ mb: 2 }}>
                  {state.selectedNotification.message}
                </Typography>
                
                {state.selectedNotification.action_url && state.selectedNotification.action_label && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const url = state.selectedNotification!.action_url!.replace(
                        /^\/schedules(\?|$)/,
                        '/schedule$1'
                      );
                      window.location.href = url;
                    }}
                  >
                    {state.selectedNotification.action_label}
                  </Button>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                if (state.selectedNotification) {
                  markAsRead(state.selectedNotification.id, !state.selectedNotification.is_read);
                }
              }}
            >
              {state.selectedNotification?.is_read ? 'Mark as Unread' : 'Mark as Read'}
            </Button>
            <Button
              variant="outlined"
              color="danger"
              onClick={() => {
                if (state.selectedNotification) {
                  deleteNotification(state.selectedNotification.id);
                  setState(prev => ({ ...prev, modalOpen: false }));
                }
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Context Menu */}
      <Menu
        anchorEl={state.anchorEl}
        open={Boolean(state.anchorEl)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        {state.selectedNotificationId && (() => {
          const selectedNotification = state.notifications.find(n => n.id === state.selectedNotificationId);
          const isRead = selectedNotification?.is_read || false;
          
          return (
            <>
              <MenuItem onClick={() => handleMenuAction(isRead ? 'mark_unread' : 'mark_read')}>
                {isRead ? <Circle sx={{ mr: 1 }} /> : <CheckCircle sx={{ mr: 1 }} />}
                {isRead ? 'Mark as Unread' : 'Mark as Read'}
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => handleMenuAction('delete')} color="danger">
                <Delete sx={{ mr: 1 }} />
                Delete
              </MenuItem>
            </>
          );
        })()}
      </Menu>
    </Card>
  );
};

export default NotificationWidget; 